import { useContext, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";
import { useHasReports } from "Flocknote/stateHooks";

import { BoardContext } from "Boards/BoardContext";
import { motion } from "framer-motion";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrErrorMessage from "UI/FnrErrorMessage";
import FnrInput from "UI/FnrInput";

import {
  getGroupsWithNestedLevel,
  getNestedGroupIDsFromParentID,
} from "../../Utils/nestedGroupUtils";
import { ACTION } from "../constants";

import SingleGroupSelector from "./GroupSelector/SingleGroupSelector";
import ReportCheckbox from "./ReportCheckbox";
import SelectGroups from "./SelectGroups";
import ShareCheckbox from "./ShareCheckbox";

export default function CopyButtonExpandedContent({
  showTitleInput,
  availableGroups,
  defaultGroupID,
  onSave,
  onCancel,
  saving,
  titleInputLabel = "New Report",
  selectedAction,
  showShareCheckbox = false,
  errors,
  setErrors,
}) {
  const buttonRef = useRef();
  const { networkName } = useNetworkMetadata();
  const [{ boardTitle, boardID }] = useContext(BoardContext);

  const [newTitle, setNewTitle] = useState(`${boardTitle} COPY`);
  const [showGroupSelectList, setShowGroupSelectList] = useState(false);
  const hasReportsFeature = useHasReports();

  /**
   * selectedGroupID can be either a single ID or an array of IDs
   * the "Share the Entire Board" option can only select one group
   * all other duplication options use a multiselector and save it as an array
   */
  const [selectedGroupID, setSelectedGroupID] = useState(
    defaultGroupID || null
  );

  const [makeReport, setMakeReport] = useState(false);
  const [share, setShare] = useState(true);

  // const useMultiGroupSelector = selectedAction === ACTION.SAVE_AS_REPORT;
  const isSingleGroupSelector =
    selectedAction === ACTION.SHARE_ENTIRE_BOARD || !hasReportsFeature;

  const handleSingleGroupSelect = (groupID) => {
    setSelectedGroupID(groupID);
    setShowGroupSelectList(false);
  };

  const groupsWithLevels = getGroupsWithNestedLevel({
    groups: availableGroups,
    everyoneGroupName: networkName,
  });

  const handleMultiGroupSelect = (groupID) => {
    const everyoneGroupID = availableGroups[0]?.groupID;
    const isEveryoneGroup = groupID === everyoneGroupID;

    const isParentGroup = groupsWithLevels.some(
      (group) => group.parentGroupID === groupID
    );

    let newGroupIDs;
    if (!selectedGroupID) {
      newGroupIDs = [groupID];
    } else if (selectedGroupID.includes(groupID)) {
      newGroupIDs = selectedGroupID.filter((id) => id !== groupID);
    } else if (isEveryoneGroup) {
      // clears all other selected groups b/c everyone encompasses them
      newGroupIDs = [groupID];
    } else if (isParentGroup) {
      // clears all children b/c they are encompassed by this group
      let childGroupIDs = getNestedGroupIDsFromParentID(
        groupID,
        groupsWithLevels
      );
      let selectedWithChildrenRemoved = selectedGroupID.filter(
        (ID) => !childGroupIDs.includes(ID)
      );
      newGroupIDs = [...selectedWithChildrenRemoved, groupID];
    } else {
      newGroupIDs = [...selectedGroupID, groupID];
    }

    // deselect everyone group if anything else was selected
    if (!isEveryoneGroup) {
      newGroupIDs = newGroupIDs.filter((id) => id !== everyoneGroupID);
    }

    if (newGroupIDs.length > 1) {
      setMakeReport(true);
      setShare(true);
    }

    setSelectedGroupID(newGroupIDs);
  };

  const handleGroupSelect = (groupID) => {
    setErrors && setErrors(undefined);
    if (isSingleGroupSelector) {
      handleSingleGroupSelect(groupID);
      return;
    }
    handleMultiGroupSelect(groupID);
  };

  const handleSave = () => {
    if (!onSave) return;

    const isMissingGroupID =
      !selectedGroupID ||
      (Array.isArray(selectedGroupID) && selectedGroupID.length === 0);

    if (isMissingGroupID) {
      setErrors && setErrors(["Please select a group or groups."]);
      return;
    }

    onSave({
      title: newTitle,
      groupID: selectedGroupID,
      makeReport,
      share,
      boardID,
    });
  };

  const getSelectedGroupNameSingle = () => {
    if (!availableGroups?.length) return "";
    if (!selectedGroupID) return "Select a group";
    const group = availableGroups.find((g) => g.groupID === selectedGroupID);
    return group?.groupName || "";
  };

  const getSelectedGroupNameMulti = () => {
    if (!availableGroups?.length) return "";
    if (!selectedGroupID?.length) return "Select groups";
    const groups = availableGroups
      .filter((g) => selectedGroupID.includes(g.groupID))
      .map((g) => g.groupName)
      .join(", ");

    if (groups.length > 30) {
      return `${groups.slice(0, 35)}...`;
    }
    return groups;
  };

  const getSelectedGroupName = () => {
    if (isSingleGroupSelector) return getSelectedGroupNameSingle();
    return getSelectedGroupNameMulti();
  };

  const handleCancelClick = () => {
    setErrors && setErrors(undefined);
    onCancel && onCancel();
  };

  const groupSelectorLabel = isSingleGroupSelector
    ? "Select Group"
    : "Select Group(s)";

  return (
    <Wrapper>
      <Content>
        {showTitleInput && (
          <SelectListWrapper>
            <Icon className="fad fa-table" />
            <InnerWrapper>
              <Label>{titleInputLabel}</Label>
              <FnrInput
                data-cy="newBoardTitle"
                value={newTitle}
                onChange={setNewTitle}
                autoFocus
                size="small"
                focusBorderColor="rgba(0,0,0,0)"
                style={{
                  border: "none",
                  paddingLeft: 0,
                  paddingTop: 4,
                }}
              />
            </InnerWrapper>
          </SelectListWrapper>
        )}
        <SelectListWrapper>
          <Icon className="fad fa-users-class" />
          <InnerWrapper>
            <Label>{groupSelectorLabel}</Label>
            {isSingleGroupSelector && (
              <>
                <Selection
                  ref={buttonRef}
                  data-cy="selectAGroup"
                  onClick={() => setShowGroupSelectList((prev) => !prev)}
                >
                  <span>{getSelectedGroupName()}</span>
                  <SelectIcon
                    className={
                      showGroupSelectList
                        ? "fas fa-chevron-right rotate"
                        : "fas fa-chevron-right"
                    }
                  />
                </Selection>
                <SingleGroupSelector
                  showGroupSelectList={showGroupSelectList}
                  buttonRef={buttonRef}
                  availableGroups={groupsWithLevels}
                  selectedGroupID={selectedGroupID}
                  handleGroupSelect={handleGroupSelect}
                  setShowGroupSelectList={setShowGroupSelectList}
                />
              </>
            )}
            {!isSingleGroupSelector && (
              <SelectGroups
                showGroupSelectList={showGroupSelectList}
                buttonRef={buttonRef}
                availableGroups={groupsWithLevels}
                selectedGroupID={selectedGroupID}
                handleGroupSelect={handleGroupSelect}
                setShowGroupSelectList={setShowGroupSelectList}
                selectedAction={selectedAction}
              />
            )}
          </InnerWrapper>
        </SelectListWrapper>

        {!isSingleGroupSelector && (
          <>
            <ReportCheckbox
              makeReport={makeReport}
              setMakeReport={setMakeReport}
              setShare={setShare}
              disabled={selectedGroupID?.length > 1}
            />

            <ShareCheckbox
              share={share}
              setShare={setShare}
              showShareCheckbox={showShareCheckbox}
              makeReport={makeReport}
            />
          </>
        )}

        <FnrErrorMessage errors={errors} />

        <ButtonsContainer>
          <FnrButton
            data-cy="duplicateBoardCancelButton"
            size="medium"
            onClick={handleCancelClick}
            disabled={saving}
          >
            Cancel
          </FnrButton>
          <FnrButton
            data-cy="duplicateBoardSaveButton"
            size="medium"
            color="green"
            onClick={handleSave}
            loading={saving}
          >
            Save
          </FnrButton>
        </ButtonsContainer>
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  margin-top: 18px;
  padding: 0 20px 0 56px;
  margin-bottom: 20px;
`;
const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Selection = styled.div`
  font-weight: 400;
  cursor: pointer;
  ${(props) =>
    props.disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
  `}
  padding-top: 4px;
`;
const SelectIcon = styled.span`
  margin-left: 8px;
  font-size: 16px;
  transition: transform 75ms;

  &.rotate {
    transform: rotate(90deg);
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const SelectListWrapper = styled.div`
  display: flex;
  gap: 12px;

  box-sizing: border-box;
  width: 100%;
  position: relative;
  color: #475b68;
  background: linear-gradient(
    -2deg,
    #ffffff 60%,
    #fdfdfd,
    #fbfbfc,
    #f8f9fa,
    #f6f7f8
  );
  border-radius: 7px;
  border: 1px solid #98a4a5;
  padding: 6px;
`;

const Icon = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-block-start: 10px;
  font-size: 22px;
  width: 30px;
`;

const InnerWrapper = styled.div`
  width: 100%;

  input {
    padding: 0;
  }
`;

const Label = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: #828c8d;
  font-weight: 400;
  line-height: 15px;
`;
